import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  LogIn, UserPlus, User, Building, Phone, Mail, AtSign, Gift
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function AuthForm({ onSuccess }) {
  const { register, login } = useAuth();
  const { t } = useLanguage();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ identifier: "", password: "", role: "advertiser", ref_code: "" });

  // Pobierz kod polecajacy z URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get("ref");
    if (refCode) {
      setForm(prev => ({ ...prev, ref_code: refCode.toUpperCase() }));
      setTab("register"); // Automatycznie przejdz do rejestracji
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.identifier.trim() || !form.password) return;
    setLoading(true);
    try {
      if (tab === "login") {
        await login(form.identifier, form.password);
        toast.success(t("auth.loginSuccess"));
      } else {
        await register(form.identifier, form.password, form.role, form.ref_code);
        toast.success(t("auth.registerSuccess"));
        if (form.ref_code) {
          toast.success("Bonus za polecenie zostal naliczony!");
        }
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="auth-form" className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md rounded-xl border shadow-lg bg-card">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 rounded-xl bg-primary mx-auto mb-3 flex items-center justify-center">
            <User className="w-6 h-6 text-primary-foreground" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-xl font-heading">Epidemos</CardTitle>
          <p className="text-xs text-muted-foreground">AI Content Management System</p>
        </CardHeader>
        <CardContent className="pt-2">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-4" data-testid="auth-tabs">
              <TabsTrigger value="login" className="gap-1.5">
                <LogIn className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("auth.login")}
              </TabsTrigger>
              <TabsTrigger value="register" className="gap-1.5">
                <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("auth.register")}
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">{t("auth.identifier")}</Label>
                <Input data-testid="auth-identifier" type="text" value={form.identifier}
                  onChange={(e) => setForm({...form, identifier: e.target.value})}
                  placeholder={t("auth.identifierPlaceholder")} />
                <p className="text-[10px] text-muted-foreground">{t("auth.identifierHint")}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">{t("auth.password")}</Label>
                <Input data-testid="auth-password" type="password" value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  placeholder="****" />
              </div>

              {tab === "register" && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">{t("auth.role")}</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                      <SelectTrigger data-testid="auth-role" className="h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="advertiser" className="text-xs">
                          <span className="flex items-center gap-1.5"><Building className="w-3 h-3" /> {t("auth.advertiser")}</span>
                        </SelectItem>
                        <SelectItem value="publisher" className="text-xs">
                          <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {t("auth.publisher")}</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs flex items-center gap-1">
                      <Gift className="w-3 h-3 text-emerald-500" /> Kod polecajacy (opcjonalnie)
                    </Label>
                    <Input 
                      data-testid="auth-refcode" 
                      type="text" 
                      value={form.ref_code}
                      onChange={(e) => setForm({...form, ref_code: e.target.value.toUpperCase()})}
                      placeholder="np. ABC12345"
                      className={form.ref_code ? "border-emerald-300 bg-emerald-50" : ""}
                    />
                    {form.ref_code && (
                      <p className="text-[10px] text-emerald-600">Osoba polecajaca otrzyma 50 Zorkow bonus!</p>
                    )}
                  </div>
                </>
              )}

              <Button data-testid="auth-submit" type="submit" className="w-full h-10 rounded-lg" disabled={loading}>
                {loading ? "..." : tab === "login" ? t("auth.loginBtn") : t("auth.registerBtn")}
              </Button>
            </form>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
